import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useSnapshot } from 'valtio';

import { currentConnectionStatus } from '@canvas/canvas-state/connection-status';
import type { ConnectionStatus } from '@canvas/model/network';
import { type BoardSyncStatus as ContentSyncStatus } from '@canvas/model/network';

import { type snackbarMessage } from './useContentStatusHeader';

export type VisualSyncStatus = 'saving' | 'saved' | 'hidden' | 'offline';

export const TIMEOUT_1000 = 1000;
export const TIMEOUT_2000 = 2000;
export const TIMEOUT_2500 = 2500;
export const TIMEOUT_4000 = 4000;
export const TIMEOUT_5000 = 5000;
export const TIMEOUT_10_000 = 10000;
export const TIMEOUT_30_000 = 30000;

const snackbarMessageId = 'content-sync-status';

const canShowOfflineSnackbar = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

export const useVisualContentSyncStatus = (
  contentId: string,
  snackbarMessages: Record<snackbarMessage, string>
): VisualSyncStatus => {
  const { connectionStatus, boardSyncStatus: contentSyncStatus } = useSnapshot(
    currentConnectionStatus
  );

  const offlineMessageShown = useRef(false);
  const offlineTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevConnectionStatus = useRef<ConnectionStatus>();
  const prevSyncStatus = useRef<ContentSyncStatus>();

  const clearOfflineTimeout = useCallback(() => {
    if (offlineTimeoutRef.current) {
      clearTimeout(offlineTimeoutRef.current);
      offlineTimeoutRef.current = null;
    }
  }, []);

  const prevContentId = useRef<string | null>();

  const initialVisualSyncStatus =
    contentSyncStatus === 'saved' ||
    contentSyncStatus === 'editToViewAccess' ||
    contentSyncStatus === 'viewToEditAccess' ||
    contentSyncStatus === 'editToViewOfflineChangesDiscarded'
      ? 'hidden'
      : contentSyncStatus;
  const [visualSyncStatus, setVisualSyncStatus] = useState<VisualSyncStatus>(
    initialVisualSyncStatus
  );

  useEffect(() => {
    let messageTimeout: NodeJS.Timeout;
    let clearMessageTimeout: NodeJS.Timeout;

    if (
      canShowOfflineSnackbar() &&
      prevConnectionStatus.current !== 'offline' &&
      connectionStatus === 'offline'
    ) {
      // Only set up the offline timeout if we don't already have one running
      if (!offlineTimeoutRef.current) {
        offlineTimeoutRef.current = setTimeout(() => {
          offlineMessageShown.current = true;
          currentSnackbarState.setMessage(snackbarMessages.offline, {
            id: snackbarMessageId
          });
          offlineTimeoutRef.current = null;
        }, TIMEOUT_30_000);
      }
    }

    if (
      offlineMessageShown.current &&
      prevConnectionStatus.current === 'offline' &&
      connectionStatus === 'online'
    ) {
      messageTimeout = setTimeout(() => {
        offlineMessageShown.current = false;
        currentSnackbarState.setMessage(snackbarMessages.online, {
          id: snackbarMessageId
        });

        clearMessageTimeout = setTimeout(() => {
          currentSnackbarState.clearMessage(snackbarMessageId);
        }, TIMEOUT_2000);
      }, TIMEOUT_1000);
    }

    return () => {
      clearTimeout(messageTimeout);
      clearTimeout(clearMessageTimeout);
    };
  }, [connectionStatus, snackbarMessages.offline, snackbarMessages.online]);

  // Clear offline timeout when coming back online
  useEffect(() => {
    if (connectionStatus === 'online') {
      clearOfflineTimeout();
    }
  }, [connectionStatus, clearOfflineTimeout]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined = undefined;

    if (contentId !== prevContentId.current) {
      currentSnackbarState.clearMessage(snackbarMessageId);

      // After loading a new board, we want to let the user if they only have view access
      if (contentSyncStatus === 'editToViewAccess') {
        currentSnackbarState.setMessage(snackbarMessages.readOnlyModeActive, {
          id: snackbarMessageId,
          showCloseButton: true,
          showAfter: TIMEOUT_2000
        });
      }
      return;
    }

    if (
      arePermissionsChanged(contentSyncStatus, prevSyncStatus.current) &&
      connectionStatus === 'online'
    ) {
      if (contentSyncStatus === 'editToViewAccess') {
        currentSnackbarState.setMessage(snackbarMessages.editToViewAccess, {
          id: snackbarMessageId,
          hideAfter: TIMEOUT_2500
        });

        currentSnackbarState.setMessage(snackbarMessages.readOnlyModeActive, {
          id: snackbarMessageId,
          showCloseButton: true,
          showAfter: TIMEOUT_4000
        });
      } else if (contentSyncStatus === 'viewToEditAccess') {
        currentSnackbarState.clearMessage(snackbarMessageId);
        currentSnackbarState.setMessage(snackbarMessages.viewToEditAccess, {
          id: snackbarMessageId,
          hideAfter: TIMEOUT_2500
        });
      } else if (contentSyncStatus === 'editToViewOfflineChangesDiscarded') {
        currentSnackbarState.setMessage(
          snackbarMessages.editToViewOfflineChangesDiscarded,
          {
            id: snackbarMessageId,
            showCloseButton: true,
            showAfter: TIMEOUT_2000
          }
        );
      }
      setVisualSyncStatus('hidden');
      return;
    }

    // online → offline - at least 10000ms in offline before represented visually
    if (visualSyncStatus !== 'offline' && connectionStatus === 'offline') {
      timeout = setTimeout(() => {
        setVisualSyncStatus('offline');
      }, TIMEOUT_10_000);
    }

    if (prevConnectionStatus.current === 'offline' && connectionStatus === 'online') {
      timeout = setTimeout(() => {
        if (visualSyncStatus !== 'hidden') {
          setVisualSyncStatus(contentSyncStatus as VisualSyncStatus);
        }
      }, TIMEOUT_1000);
      return;
    }

    // offline state takes precendence
    if (visualSyncStatus === 'offline' || prevConnectionStatus.current === 'offline') {
      return;
    }

    // saved → saving - at least 5000 in saving before represented visually
    if (prevSyncStatus.current === 'saved' && contentSyncStatus === 'saving') {
      timeout = setTimeout(() => {
        setVisualSyncStatus('saving');
      }, TIMEOUT_5000);
    }

    // saving → saved - at least 1000 in saved before represented visually
    if (
      visualSyncStatus !== 'hidden' &&
      prevSyncStatus.current === 'saving' &&
      contentSyncStatus === 'saved'
    ) {
      timeout = setTimeout(() => {
        setVisualSyncStatus('saved');
      }, TIMEOUT_1000);
    }

    return () => timeout && clearTimeout(timeout);
  }, [
    contentSyncStatus,
    connectionStatus,
    prevSyncStatus,
    visualSyncStatus,
    contentId,
    prevContentId,
    snackbarMessages.readOnlyModeActive,
    snackbarMessages.editToViewAccess,
    snackbarMessages.viewToEditAccess,
    snackbarMessages.editToViewOfflineChangesDiscarded
  ]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (visualSyncStatus === 'saved') {
      timeout = setTimeout(() => {
        setVisualSyncStatus('hidden');
      }, TIMEOUT_5000);
    }

    return () => clearTimeout(timeout);
  });

  useEffect(() => {
    prevSyncStatus.current = contentSyncStatus;
  }, [contentSyncStatus]);

  useEffect(() => {
    prevConnectionStatus.current = connectionStatus;
  }, [connectionStatus]);

  useEffect(() => {
    prevContentId.current = contentId;
  }, [contentId]);

  return visualSyncStatus;
};

const arePermissionsChanged = (
  contentStatus: ContentSyncStatus,
  prevContentStatus?: ContentSyncStatus
) => {
  return (
    [
      'editToViewAccess',
      'viewToEditAccess',
      'editToViewOfflineChangesDiscarded'
    ].includes(contentStatus) && contentStatus !== prevContentStatus
  );
};
